/**
 * Visual-engine Canvas wrapper (PRD §5.2 camera, §6.3 visual bar).
 *
 * `<Stage>` is the seam between React-DOM and Three. It owns:
 *   - the WebGL canvas + size
 *   - the camera (tilted near-orthographic; orbit + zoom via drei
 *     `OrbitControls` for P1+; snap-to-mech in P6)
 *   - the lighting preset (p1-basic vs magica-voxel)
 *   - the optional post-FX stack
 *
 * The scene tree itself is passed as children so consumers (stories,
 * `ReplayScene`, the future marketing replay viewer) compose their
 * own world content.
 */

import { Suspense, useRef, type ReactNode } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera, Sparkles } from "@react-three/drei";
import { ACESFilmicToneMapping, PCFSoftShadowMap, SRGBColorSpace } from "three";
import { Lights, type LightingPreset } from "./Lights";
import { PostFx, type PostFxProps } from "./PostFx";
import { shakeOffset } from "./cameraShake";

export interface StageProps {
  children: ReactNode;
  /** "p1-basic" → ambient + dir light; "magica-voxel" → warm key +
   *  cool ambient + warm HDRI + soft shadows (canonical preset). */
  lighting?: LightingPreset;
  /** When true, mount the PostFx composer. Defaults to true for any
   *  preset other than "p1-basic". */
  postFx?: boolean | PostFxProps;
  /** Override canvas background color (e.g., for a transparent embed). */
  background?: string;
  /** Disable orbit controls for kiosk-style screenshots. */
  orbitEnabled?: boolean;
  /** Rising-edge `performance.now()` of a kill event to drive the FE-30
   *  P6 cinematic light pulse. `null` ⇒ no pulse. */
  killPulseStartedAt?: number | null;
  /** Deterministic seed for the FE-30 Q5 camera-shake direction. Pass
   *  the kill tick (or any stable derivative of matchSeed+victim+tick)
   *  so two viewings of the same replay produce the same shake. */
  cameraShakeSeed?: number;
  /** A11y opt-out — when true, suppress the camera shake. The kill
   *  light pulse still fires (info-bearing). */
  reducedMotion?: boolean;
  /** Rising-edge `performance.now()` of a focus-side hit to drive the
   *  FE-30 Q4 vignette pulse. `null` ⇒ no pulse. */
  vignettePulseStartedAt?: number | null;
  /** Rising-edge `performance.now()` of a focus-side hit, threaded
   *  through to PostFx for the B1 chromatic-aberration micro-spike. */
  hitPulseStartedAt?: number | null;
}

export function Stage({
  children,
  lighting = "magica-voxel",
  postFx,
  background = "#0b1020",
  orbitEnabled = true,
  killPulseStartedAt = null,
  cameraShakeSeed = 0,
  reducedMotion = false,
  vignettePulseStartedAt = null,
  hitPulseStartedAt = null,
}: StageProps) {
  const wantsPost =
    postFx === undefined ? lighting !== "p1-basic" : !!postFx;
  // Both vignette pulse + kill pulse + hit pulse thread through to
  // PostFx so the chromatic-aberration spike rides the same timers
  // that already drive the vignette + kill light.
  const postProps: PostFxProps =
    typeof postFx === "object" && postFx !== null
      ? { ...postFx, vignettePulseStartedAt, killPulseStartedAt, hitPulseStartedAt }
      : { vignettePulseStartedAt, killPulseStartedAt, hitPulseStartedAt };

  return (
    <Canvas
      shadows={lighting === "p1-basic" ? false : { type: PCFSoftShadowMap }}
      gl={{
        antialias: true,
        toneMapping: ACESFilmicToneMapping,
        toneMappingExposure: 1,
        outputColorSpace: SRGBColorSpace,
      }}
      style={{ background, width: "100%", height: "100%" }}
      dpr={[1, 2]}
    >
      <PerspectiveCamera makeDefault position={[10, 12, 14]} fov={35} />
      <OrbitControls
        enableDamping
        dampingFactor={0.1}
        enablePan={orbitEnabled}
        enableRotate={orbitEnabled}
        enableZoom={orbitEnabled}
        target={[0, 0.4, 0]}
        maxPolarAngle={Math.PI / 2.05}
      />
      {/* drei v10's `<Environment>` lazy-loads HDRIs (suspends);
          without a Suspense ancestor inside the Canvas R3F v9 throws
          while the promise is in-flight and the Storybook error
          overlay then tries to JSON.stringify a partially-mounted
          Three.js scene graph ("Converting circular structure to
          JSON"). The boundary keeps the loader silent until ready. */}
      <Suspense fallback={null}>
        <Lights preset={lighting} killPulseStartedAt={killPulseStartedAt} />
        {!reducedMotion && killPulseStartedAt !== null ? (
          <CameraShake startedAt={killPulseStartedAt} seed={cameraShakeSeed} />
        ) : null}
        {/* B1 — ambient dust motes drifting through the arena. The
            scale is a coarse 16×6×16 box centered on the board so the
            motes fill the visible space without bunching near the
            chassis. drei's Sparkles is an optimized instanced field
            (~zero overhead at this count). Suppressed for p1-basic
            lighting (debug preset). */}
        {lighting !== "p1-basic" && !reducedMotion ? (
          <Sparkles
            count={80}
            size={2}
            scale={[16, 6, 16]}
            position={[0, 2, 0]}
            speed={0.18}
            opacity={0.45}
            color="#d4e0ff"
            noise={1}
          />
        ) : null}
        {children}
        {wantsPost ? <PostFx {...postProps} /> : null}
      </Suspense>
    </Canvas>
  );
}

/**
 * One-shot deterministic camera shake. Lives inside the Canvas so it
 * has R3F context. Each frame we add the *delta* from the previous
 * frame's offset, so the shake composes additively with OrbitControls'
 * own camera writes — when the shake expires (offset = 0) the delta
 * subtracts the previous offset and the camera returns to neutral.
 */
function CameraShake({ startedAt, seed }: { startedAt: number; seed: number }) {
  const camera = useThree((s) => s.camera);
  const prevRef = useRef<{ dx: number; dy: number; dz: number }>({ dx: 0, dy: 0, dz: 0 });

  useFrame(() => {
    const offset = shakeOffset(startedAt, seed, performance.now());
    const prev = prevRef.current;
    camera.position.x += offset.dx - prev.dx;
    camera.position.y += offset.dy - prev.dy;
    camera.position.z += offset.dz - prev.dz;
    prevRef.current = offset;
  });
  return null;
}
